import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Wifi, WifiOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { OutcomeSelector } from './orderbook/OutcomeSelector';
import { OrderbookView } from './orderbook/OrderbookView';
import { OrderForm } from './orderbook/OrderForm';
import { UserOrdersAndPositions } from './orderbook/UserOrdersAndPositions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (cents) => {
  if (cents >= 100) return `$${(cents / 100).toFixed(2)}`;
  return `${cents}¢`;
};

const OrderbookModal = ({ isOpen, onClose, market, isSubscribed, onShowSubscribe }) => {
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const [activeTab, setActiveTab] = useState('buy');
  const [outcome, setOutcome] = useState('YES');
  const [orderType, setOrderType] = useState('limit');
  const [price, setPrice] = useState(50);
  const [quantity, setQuantity] = useState(10);
  const [orderbook, setOrderbook] = useState(null);
  const [recentTrades, setRecentTrades] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [userPositions, setUserPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [showSubscribePrompt, setShowSubscribePrompt] = useState(false);
  const wsRef = useRef(null);

  const isResolved = market?.status === 'RESOLVED';

  const getWsUrl = useCallback(() => {
    const url = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    return `${url}/api/ws?market_id=${market?.id}`;
  }, [market?.id]);

  // WebSocket for live updates
  useEffect(() => {
    if (!isOpen || !market?.id) return;

    const connectWs = () => {
      try {
        const ws = new WebSocket(getWsUrl());

        ws.onopen = () => {
          console.log('Orderbook WebSocket connected');
          setWsConnected(true);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'orderbook_update' && data.market_id === market.id) {
              setOrderbook(prev => ({ ...prev, ...data.orderbook }));
            }

            if (data.type === 'trade' && data.market_id === market.id) {
              setRecentTrades(prev => [data.trade, ...prev].slice(0, 20));
            }
          } catch (e) {
            console.error('WS message parse error:', e);
          }
        };

        ws.onclose = () => {
          console.log('Orderbook WebSocket disconnected');
          setWsConnected(false);
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };

        wsRef.current = ws;
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
      }
    };

    connectWs();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, market?.id, getWsUrl]);

  const fetchOrderbook = useCallback(async () => {
    if (!market?.id) return;

    try {
      const [obRes, tradesRes, volumeRes] = await Promise.all([
        fetch(`${API_URL}/api/orderbook/${market.id}`),
        fetch(`${API_URL}/api/orderbook/${market.id}/trades?limit=20`),
        fetch(`${API_URL}/api/orderbook/${market.id}/volume`)
      ]);

      if (obRes.ok) {
        const ob = await obRes.json();
        setOrderbook(prev => ({ ...prev, ...ob }));
      }

      if (tradesRes.ok) {
        const trades = await tradesRes.json();
        setRecentTrades(trades.trades || []);
      }

      if (volumeRes.ok) {
        const volume = await volumeRes.json();
        setOrderbook(prev => ({ ...prev, volume }));
      }
    } catch (err) {
      console.error('Error fetching orderbook:', err);
    } finally {
      setLoading(false);
    }
  }, [market]);

  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated || !market?.id) return;

    try {
      const [ordersRes, positionsRes, walletRes] = await Promise.all([
        fetch(`${API_URL}/api/orderbook/user/orders?market_id=${market.id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/orderbook/user/positions`, { credentials: 'include' }),
        fetch(`${API_URL}/api/wallet`, { credentials: 'include' })
      ]);

      if (ordersRes.ok) {
        const orders = await ordersRes.json();
        setUserOrders(orders.orders || []);
      }

      if (positionsRes.ok) {
        const positions = await positionsRes.json();
        setUserPositions(positions.positions?.filter(p => p.market_id === market.id) || []);
      }

      if (walletRes.ok) {
        const wallet = await walletRes.json();
        setWalletBalance(wallet.balance || 100000000);
      } else {
        setWalletBalance(100000000);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setWalletBalance(100000000);
    }
  }, [isAuthenticated, market]);

  useEffect(() => {
    if (isOpen && market?.id) {
      fetchOrderbook();
      fetchUserData();

      const interval = setInterval(() => {
        fetchOrderbook();
        if (isAuthenticated) fetchUserData();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isOpen, market?.id, fetchOrderbook, fetchUserData, isAuthenticated]);

  // Derived order/price values
  const bestAsk = outcome === 'YES' ? (orderbook?.yes?.best_ask || 100) : (orderbook?.no?.best_ask || 100);
  const bestBid = outcome === 'YES' ? (orderbook?.yes?.best_bid || 0) : (orderbook?.no?.best_bid || 0);

  const effectivePrice = orderType === 'market'
    ? (activeTab === 'buy' ? bestAsk : bestBid)
    : price;

  const totalCost = effectivePrice * quantity;
  const potentialPayout = quantity * 100;
  const potentialProfit = potentialPayout - totalCost;

  const rawYesPrice = orderbook?.yes?.mid_price || market?.yes_price || 50;
  const yesPrice = Math.min(99, Math.max(1, Math.round(rawYesPrice)));
  const noPrice = 100 - yesPrice;

  const handlePlaceOrder = async () => {
    if (!isAuthenticated) {
      loginWithGoogle();
      return;
    }

    if (!isSubscribed) {
      setShowSubscribePrompt(true);
      setError(null);
      return;
    }

    if (orderType === 'market') {
      if (activeTab === 'buy' && bestAsk >= 100) {
        setError('No sell orders available for market buy');
        return;
      }
      if (activeTab === 'sell' && bestBid <= 0) {
        setError('No buy orders available for market sell');
        return;
      }
    }

    setSubmitting(true);
    setError(null);

    try {
      const headers = { 'Content-Type': 'application/json' };
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${API_URL}/api/orderbook/order`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          market_id: market.id,
          side: activeTab,
          outcome: outcome,
          price: orderType === 'market' ? effectivePrice : price,
          quantity: quantity,
          order_type: orderType
        })
      });

      if (response.ok) {
        setError(null);
        setShowSubscribePrompt(false);
        fetchOrderbook();
        fetchUserData();
      } else {
        const errorData = await response.json();
        if (response.status === 403 && errorData.detail?.includes('Subscription required')) {
          setShowSubscribePrompt(true);
          setError(null);
        } else {
          setError(errorData.detail || 'Failed to place order');
          setShowSubscribePrompt(false);
        }
      }
    } catch (err) {
      console.error('Order placement error:', err);
      setError('Network error - please try again');
      setShowSubscribePrompt(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/orderbook/order/${orderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        await fetchUserData();
      }
    } catch (err) {
      console.error('Failed to cancel order:', err);
    }
  };

  const handleShowSubscribe = () => {
    setShowSubscribePrompt(false);
    onClose();
    if (onShowSubscribe) onShowSubscribe();
  };

  if (!market) return null;

  const renderResolvedBadge = () => {
    const raw = (market.resolved_outcome || market.winning_outcome || '').toString().toUpperCase();
    const isTie = raw === 'TIE' || raw === 'DRAW' || raw === 'CANCELED' || raw === 'CANCELLED';
    const winner = raw === 'YES' ? 'YES' : raw === 'NO' ? 'NO' : isTie ? 'TIE' : '';
    const cls =
      winner === 'YES' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
      winner === 'NO' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
      winner === 'TIE' ? 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30' :
      'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full border ${cls}`}>
        {winner ? `${winner} WON` : 'RESOLVED'}
      </span>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden bg-[#050810] border-[#1e2a45] max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-lg font-bold pr-8 line-clamp-2 text-white flex items-center gap-2">
              {market.title}
              {isResolved && renderResolvedBadge()}
              <span className={`ml-auto flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                wsConnected
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-gray-500/20 text-gray-400'
              }`}>
                {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                {wsConnected ? 'Live' : 'Offline'}
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Trade prediction for {market.title}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#3B82F6]" />
            </div>
          ) : (
            <div className="space-y-4">
              <OutcomeSelector
                outcome={outcome}
                setOutcome={setOutcome}
                yesPrice={yesPrice}
                noPrice={noPrice}
                orderbook={orderbook}
              />

              <OrderbookView
                outcome={outcome}
                orderbook={orderbook}
                recentTrades={recentTrades}
              />

              {!isResolved && (
                <OrderForm
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  outcome={outcome}
                  orderType={orderType}
                  setOrderType={setOrderType}
                  price={price}
                  setPrice={setPrice}
                  quantity={quantity}
                  setQuantity={setQuantity}
                  effectivePrice={effectivePrice}
                  bestAsk={bestAsk}
                  bestBid={bestBid}
                  totalCost={totalCost}
                  potentialPayout={potentialPayout}
                  potentialProfit={potentialProfit}
                  isAuthenticated={isAuthenticated}
                  walletBalance={walletBalance}
                  userPositions={userPositions}
                  error={error}
                  showSubscribePrompt={showSubscribePrompt}
                  onCloseSubscribePrompt={() => setShowSubscribePrompt(false)}
                  onShowSubscribe={handleShowSubscribe}
                  submitting={submitting}
                  onPlaceOrder={handlePlaceOrder}
                />
              )}

              <UserOrdersAndPositions
                isAuthenticated={isAuthenticated}
                userOrders={userOrders}
                userPositions={userPositions}
                yesPrice={yesPrice}
                noPrice={noPrice}
                onCancelOrder={handleCancelOrder}
              />

              {orderbook?.volume && (
                <div className="flex justify-between text-xs text-gray-500 pt-2 border-t border-[#1e2a45]">
                  <span>24h Volume: {formatCurrency(orderbook.volume.total_volume || 0)}</span>
                  <span>Total Trades: {orderbook.volume.total_trades}</span>
                  <span>Contracts: {orderbook.volume.total_contracts}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderbookModal;
